import {
  deleteMyWebPushSubscription,
  getWebPushPublicKey,
  listMyWebPushSubscriptions,
  saveMyWebPushSubscription,
} from '../api/notificationApi';

export const isWebPushSupported = () => (
  typeof window !== 'undefined'
  && 'Notification' in window
  && 'serviceWorker' in navigator
  && 'PushManager' in window
);

export const getNotificationPermission = () => {
  if (!isWebPushSupported()) return 'unsupported';
  return Notification.permission;
};

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }
  return outputArray;
};

export const getWebPushRegistration = async () => {
  if (!isWebPushSupported()) return null;
  return navigator.serviceWorker.register('/web-push-sw.js');
};

export const getCurrentBrowserPushSubscription = async () => {
  const registration = await getWebPushRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
};

export const loadBrowserPushState = async () => {
  if (!isWebPushSupported()) {
    return {
      supported: false,
      permission: 'unsupported',
      configured: false,
      subscription: null,
      serverSubscriptions: [],
      serverSubscription: null,
    };
  }
  const [keyData, subscriptionsData, browserSubscription] = await Promise.all([
    getWebPushPublicKey(),
    listMyWebPushSubscriptions(),
    getCurrentBrowserPushSubscription(),
  ]);
  const serverSubscriptions = subscriptionsData?.subscriptions || [];
  const endpoint = browserSubscription?.endpoint;
  const serverSubscription = endpoint
    ? serverSubscriptions.find((subscription) => subscription.endpoint === endpoint) || null
    : null;
  return {
    supported: true,
    permission: getNotificationPermission(),
    configured: Boolean(keyData?.configured && keyData?.publicKey),
    publicKey: keyData?.publicKey || null,
    subscription: browserSubscription,
    serverSubscriptions,
    serverSubscription,
  };
};

export const enableBrowserPushNotifications = async () => {
  if (!isWebPushSupported()) {
    const error = new Error('Este navegador no soporta notificaciones push.');
    error.code = 'UNSUPPORTED';
    throw error;
  }
  const keyData = await getWebPushPublicKey();
  if (!keyData?.configured || !keyData?.publicKey) {
    const error = new Error('Las notificaciones push no estan configuradas en el servidor.');
    error.code = 'NOT_CONFIGURED';
    throw error;
  }
  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (permission !== 'granted') {
    const error = new Error('Permiso de notificaciones no concedido.');
    error.code = 'PERMISSION_DENIED';
    throw error;
  }
  const registration = await getWebPushRegistration();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
    });
  }
  const response = await saveMyWebPushSubscription(subscription.toJSON());
  return {
    browserSubscription: subscription,
    serverSubscription: response?.subscription || null,
  };
};

export const disableBrowserPushNotifications = async () => {
  if (!isWebPushSupported()) return null;
  const state = await loadBrowserPushState();
  if (state.serverSubscription?.id) {
    await deleteMyWebPushSubscription(state.serverSubscription.id);
  }
  if (state.subscription) {
    await state.subscription.unsubscribe();
  }
  return true;
};
